import {
  AloftPrice,
  TKV2_ORDER_ATTACHMENT_API,
  expected,
  schAloftPrice,
} from '@aloft/toolkit-shared';

import { useMemo } from 'react';

import { useGetTkv2ReconciliationAttachmentAPI } from '../../../hooks/api/toolkitComponents.ts';
import { selectOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';

type UseDesktopReconciliationComparisonProps = {
  opinionOfMarketValue?: AloftPrice;
  opinionOfAfterRepairValue?: AloftPrice;
};

const getDelta = (currentValue?: AloftPrice, priorValue?: AloftPrice) => {
  if (!currentValue || !priorValue) return undefined;
  return currentValue.sub(priorValue);
};

export const useDesktopReconciliationComparison = ({
  opinionOfMarketValue,
  opinionOfAfterRepairValue,
}: UseDesktopReconciliationComparisonProps) => {
  const order = useAppSelector(selectOrder);
  const priorDesktopOrderID = order.previousDesktopOrderID ?? order.revisionData?.originalOrderId;
  const isHybridOrder = order.inspectionData?.hasInspection === true;

  const priorDesktopReconciliationQuery = useGetTkv2ReconciliationAttachmentAPI(
    {
      pathParams: {
        orderID: priorDesktopOrderID ?? '',
      },
    },
    {
      enabled: isHybridOrder && !!priorDesktopOrderID,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  return useMemo(() => {
    if (!priorDesktopReconciliationQuery.data || priorDesktopReconciliationQuery.error) {
      return {
        isHybridOrder,
        priorDesktopOrderID,
        priorDesktopAiv: undefined,
        priorDesktopArv: undefined,
        aivDelta: undefined,
        arvDelta: undefined,
        hasAivDiscrepancy: false,
        hasArvDiscrepancy: false,
      };
    }

    const parsed = TKV2_ORDER_ATTACHMENT_API.RECONCILIATION.GET.RESPONSE.parse(
      expected(priorDesktopReconciliationQuery.data),
    );

    const priorDesktopAiv = schAloftPrice.optional().parse(parsed.body.opinionOfMarketValue);
    const priorDesktopArv = schAloftPrice.optional().parse(parsed.body.opinionOfAfterRepairValue);
    const aivDelta = getDelta(opinionOfMarketValue, priorDesktopAiv);
    const arvDelta = getDelta(opinionOfAfterRepairValue, priorDesktopArv);

    return {
      isHybridOrder,
      priorDesktopOrderID,
      priorDesktopAiv,
      priorDesktopArv,
      aivDelta,
      arvDelta,
      hasAivDiscrepancy: !!aivDelta && aivDelta.toDollarLike() !== 0,
      hasArvDiscrepancy: !!arvDelta && arvDelta.toDollarLike() !== 0,
    };
  }, [
    isHybridOrder,
    opinionOfAfterRepairValue,
    opinionOfMarketValue,
    priorDesktopOrderID,
    priorDesktopReconciliationQuery.data,
    priorDesktopReconciliationQuery.error,
  ]);
};
